import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Users, MapPin, Target, Plus, Building2, Lock, Sparkles } from "lucide-react";
import api, { apiError } from "../lib/api";

export function FollowersModal({ onClose }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/institute/followers").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="followers-modal" className="nb bg-white w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b-2 border-[#171B33]">
          <h3 className="font-display font-extrabold flex items-center gap-2"><Users className="w-5 h-5" /> Followers ({list?.length ?? "…"})</h3>
          <button data-testid="followers-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-1">
          {list?.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">No followers yet — share your profile link to grow.</p>}
          {(list || []).map((f) => (
            <div key={f.id} data-testid={`follower-row-${f.id}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50">
              <span className="w-9 h-9 rounded-xl border-2 border-[#171B33] flex items-center justify-center font-extrabold" style={{ background: "#93C5FD" }}>{f.name?.[0]}</span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm">{f.name}</p>
                <p className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  {f.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {f.city}</span>}
                  {f.future_goal && <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {f.future_goal}</span>}
                </p>
              </div>
              <span className="text-[11px] text-slate-400">{(f.followed_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PlanCard({ features, planExpires }) {
  const isFree = features?.plan === "free";
  const items = [
    ["Posts / day", features?.posts_per_day || 0], ["Add teachers", features?.add_teachers], ["Add students", features?.add_students],
    ["Gallery", features?.gallery], ["WhatsApp apply", features?.whatsapp], ["MCQ tests", features?.mcq], ["Notes", features?.notes], ["Listings", features?.max_listings || 1],
  ];
  return (
    <div data-testid="plan-card" className="nb p-5" style={{ background: isFree ? "#fff" : "#F2FBE3" }}>
      <div className="flex items-center justify-between">
        <p className="font-display font-extrabold flex items-center gap-2">{isFree ? <Lock className="w-4 h-4" /> : <Sparkles className="w-4 h-4 text-lime-600" />} {features?.label || "Free"} plan</p>
        {!isFree && planExpires && <span className="text-xs text-slate-500">until {planExpires.slice(0, 10)}</span>}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {items.map(([k, v]) => (
          <div key={k} className="nb-flat p-2" style={{ borderRadius: 10, opacity: v ? 1 : 0.5 }}>
            <p className="text-slate-500">{k}</p>
            <p className="font-bold">{typeof v === "boolean" ? (v ? "Included" : "—") : v}</p>
          </div>
        ))}
      </div>
      {isFree && <p className="text-xs text-slate-600 mt-3">Free listing includes profile, courses, batches and receiving verified student reviews. Choose a plan below to unlock the rest.</p>}
    </div>
  );
}

export function ListingSwitcher({ current, onSwitch }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "coaching", city: "", area: "" });
  const load = () => api.get("/institute/listings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const canAdd = data.listings.length < data.max_listings;
  const create = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/institute/listings", form); toast.success(r.data.message); setOpen(false); load(); onSwitch(r.data.id); }
    catch (err) { toast.error(apiError(err)); }
  };
  if (data.listings.length <= 1 && !canAdd) return null;
  return (
    <div data-testid="listing-switcher" className="nb-flat p-3 flex flex-wrap items-center gap-2" style={{ borderRadius: 12 }}>
      <Building2 className="w-4 h-4 text-slate-500" />
      {data.listings.map((l) => (
        <button key={l.id} data-testid={`listing-tab-${l.id}`} onClick={() => onSwitch(l.id)} className="nb-btn nb-btn-sm" style={{ background: l.id === current ? "#A3E635" : "#fff" }}>{l.name} <span className="text-slate-500 font-normal">· {l.area}</span></button>
      ))}
      <span className="text-xs text-slate-500 ml-auto">{data.listings.length}/{data.max_listings} listings</span>
      {canAdd && <button data-testid="listing-add-btn" onClick={() => setOpen(true)} className="nb-btn nb-btn-sm"><Plus className="w-4 h-4" /> Add branch</button>}
      {open && (
        <form onSubmit={create} className="w-full grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
          <input data-testid="listing-name-input" required placeholder="Branch name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="nb-input sm:col-span-2" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="nb-input"><option value="coaching">Coaching</option><option value="school">School</option><option value="college">College</option></select>
          <input data-testid="listing-city-input" required placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="nb-input" />
          <input data-testid="listing-area-input" required placeholder="Area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="nb-input" />
          <button data-testid="listing-create-btn" className="nb-btn nb-btn-lime nb-btn-sm sm:col-span-5">Create branch listing</button>
        </form>
      )}
    </div>
  );
}
